import type {
  FunnelStageConfig,
  FunnelTemplateId,
  ProjectionInputs,
  ScenarioSnapshot,
  TrafficChannel,
} from "@/lib/funnel-planner/schema";
import { DEFAULT_PROJECTION_MONTHS } from "@/lib/funnel-planner/schema";

export type FunnelBlueprintCategory = "call" | "webinar" | "commerce" | "saas" | "local" | "ai" | "agency" | "compliance";

export type FunnelBlueprintStage = {
  id: string;
  stageType: string;
  conversionRate: number;
  waitDays?: number;
  requiredStage?: boolean;
  dueInDays?: number;
};

export type FunnelBlueprintChannel = {
  id: TrafficChannel["id"];
  monthlyVisitors: number;
  acquisitionType: TrafficChannel["acquisitionType"];
  cpc?: number;
  cpm?: number;
  ctr?: number;
  flatMonthlyFee?: number;
};

export type FunnelBlueprintPreset = {
  id: string;
  templateId: FunnelTemplateId;
  category: FunnelBlueprintCategory;
  stages: readonly FunnelBlueprintStage[];
  trafficChannels: readonly FunnelBlueprintChannel[];
  projectionInputs: ProjectionInputs;
  scenarioLift: {
    cvrMultiplier: number;
    visitorMultiplier: number;
    revenueMultiplier: number;
  };
};

export const FUNNEL_BLUEPRINT_PRESETS: readonly FunnelBlueprintPreset[] = [
  {
    id: "strategy-call-closer",
    templateId: "high-ticket-coaching",
    category: "call",
    stages: [
      { id: "vsl", stageType: "vsl-page", conversionRate: 34, dueInDays: 7 },
      { id: "application", stageType: "application-page", conversionRate: 26, dueInDays: 10 },
      { id: "booking", stageType: "booking-page", conversionRate: 58, dueInDays: 12 },
      { id: "discovery", stageType: "discovery-call", conversionRate: 42, dueInDays: 14 },
      { id: "proposal", stageType: "proposal", conversionRate: 62, dueInDays: 18 },
      { id: "close", stageType: "order-form", conversionRate: 78, dueInDays: 21 },
    ],
    trafficChannels: [
      { id: "google_organic", monthlyVisitors: 550, acquisitionType: "organic" },
      { id: "youtube", monthlyVisitors: 300, acquisitionType: "organic" },
      { id: "retargeting", monthlyVisitors: 150, acquisitionType: "cpc", cpc: 1.8 },
    ],
    projectionInputs: { projectionMonths: DEFAULT_PROJECTION_MONTHS, monthlyVisitors: 1_000, revenuePerLead: 5_000, reinvestmentPercent: 5, reinvestmentCap: 4_000 },
    scenarioLift: { cvrMultiplier: 1.16, visitorMultiplier: 1.12, revenueMultiplier: 1.05 },
  },
  {
    id: "evergreen-webinar-engine",
    templateId: "b2b-enterprise",
    category: "webinar",
    stages: [
      { id: "registration", stageType: "webinar-registration", conversionRate: 28, dueInDays: 7 },
      { id: "live", stageType: "webinar-live", conversionRate: 48, dueInDays: 10 },
      { id: "replay", stageType: "webinar-replay", conversionRate: 34, dueInDays: 12, requiredStage: false },
      { id: "application", stageType: "application-page", conversionRate: 13, dueInDays: 16 },
      { id: "demo", stageType: "discovery-call", conversionRate: 45, dueInDays: 20 },
      { id: "proposal", stageType: "proposal", conversionRate: 55, dueInDays: 24 },
      { id: "contract", stageType: "order-form", conversionRate: 72, dueInDays: 30 },
    ],
    trafficChannels: [
      { id: "linkedin", monthlyVisitors: 700, acquisitionType: "organic" },
      { id: "linkedin_ads", monthlyVisitors: 450, acquisitionType: "cpc", cpc: 6 },
      { id: "email_newsletter", monthlyVisitors: 650, acquisitionType: "organic" },
    ],
    projectionInputs: { projectionMonths: DEFAULT_PROJECTION_MONTHS, monthlyVisitors: 1_800, revenuePerLead: 9_500, reinvestmentPercent: 8, reinvestmentCap: 8_000 },
    scenarioLift: { cvrMultiplier: 1.12, visitorMultiplier: 1.2, revenueMultiplier: 1.08 },
  },
  {
    id: "tripwire-slo-ladder",
    templateId: "ecommerce-dtc",
    category: "commerce",
    stages: [
      { id: "landing", stageType: "landing-page", conversionRate: 42, dueInDays: 6 },
      { id: "optin", stageType: "opt-in-page", conversionRate: 38, dueInDays: 8 },
      { id: "tripwire", stageType: "tripwire-page", conversionRate: 9, dueInDays: 11 },
      { id: "bump", stageType: "order-bump", conversionRate: 24, dueInDays: 13, requiredStage: false },
      { id: "upsell", stageType: "upsell", conversionRate: 18, dueInDays: 15, requiredStage: false },
      { id: "nurture", stageType: "email-sequence", conversionRate: 12, waitDays: 4, dueInDays: 18 },
    ],
    trafficChannels: [
      { id: "facebook_ads", monthlyVisitors: 1_800, acquisitionType: "cpc", cpc: 1.4 },
      { id: "google_ads", monthlyVisitors: 900, acquisitionType: "cpc", cpc: 2.1 },
      { id: "email_newsletter", monthlyVisitors: 700, acquisitionType: "organic" },
    ],
    projectionInputs: { projectionMonths: DEFAULT_PROJECTION_MONTHS, monthlyVisitors: 3_400, revenuePerLead: 220, reinvestmentPercent: 12, reinvestmentCap: 7_500 },
    scenarioLift: { cvrMultiplier: 1.18, visitorMultiplier: 1.15, revenueMultiplier: 1.12 },
  },
  {
    id: "plg-trial-activation",
    templateId: "saas-plg",
    category: "saas",
    stages: [
      { id: "landing", stageType: "landing-page", conversionRate: 36, dueInDays: 5 },
      { id: "pricing", stageType: "pricing-page", conversionRate: 22, dueInDays: 8 },
      { id: "trial", stageType: "free-trial", conversionRate: 32, dueInDays: 10 },
      { id: "onboarding", stageType: "onboarding-checklist", conversionRate: 58, dueInDays: 12 },
      { id: "activation", stageType: "activation-event", conversionRate: 44, dueInDays: 14 },
      { id: "upgrade", stageType: "in-app-upgrade", conversionRate: 16, dueInDays: 18 },
    ],
    trafficChannels: [
      { id: "google_organic", monthlyVisitors: 2_200, acquisitionType: "organic" },
      { id: "review_sites", monthlyVisitors: 450, acquisitionType: "flat_fee", flatMonthlyFee: 1_200 },
      { id: "google_ads", monthlyVisitors: 800, acquisitionType: "cpc", cpc: 4.5 },
    ],
    projectionInputs: { projectionMonths: DEFAULT_PROJECTION_MONTHS, monthlyVisitors: 3_450, revenuePerLead: 1_200, reinvestmentPercent: 10, reinvestmentCap: 6_000 },
    scenarioLift: { cvrMultiplier: 1.14, visitorMultiplier: 1.18, revenueMultiplier: 1.08 },
  },
  {
    id: "multi-location-lead-engine",
    templateId: "franchise-multi-location",
    category: "local",
    stages: [
      { id: "local-serp", stageType: "landing-page", conversionRate: 52, dueInDays: 7 },
      { id: "location-page", stageType: "product-detail-page", conversionRate: 36, dueInDays: 10 },
      { id: "quote", stageType: "application-page", conversionRate: 18, dueInDays: 14 },
      { id: "call", stageType: "discovery-call", conversionRate: 48, dueInDays: 18 },
      { id: "onboard", stageType: "proposal", conversionRate: 65, dueInDays: 24 },
    ],
    trafficChannels: [
      { id: "google_organic", monthlyVisitors: 1_500, acquisitionType: "organic" },
      { id: "google_ads", monthlyVisitors: 600, acquisitionType: "cpc", cpc: 3.2 },
      { id: "review_sites", monthlyVisitors: 450, acquisitionType: "organic" },
    ],
    projectionInputs: { projectionMonths: DEFAULT_PROJECTION_MONTHS, monthlyVisitors: 2_550, revenuePerLead: 3_200, reinvestmentPercent: 7, reinvestmentCap: 5_000 },
    scenarioLift: { cvrMultiplier: 1.1, visitorMultiplier: 1.16, revenueMultiplier: 1.04 },
  },
  {
    id: "ai-search-demand-capture",
    templateId: "ai-search-geo",
    category: "ai",
    stages: [
      { id: "citation", stageType: "blog-post", conversionRate: 62, dueInDays: 6 },
      { id: "answer-click", stageType: "landing-page", conversionRate: 28, dueInDays: 9 },
      { id: "comparison", stageType: "case-study", conversionRate: 24, dueInDays: 12 },
      { id: "lead", stageType: "opt-in-page", conversionRate: 17, dueInDays: 15 },
      { id: "call", stageType: "discovery-call", conversionRate: 42, dueInDays: 20 },
      { id: "close", stageType: "proposal", conversionRate: 58, dueInDays: 26 },
    ],
    trafficChannels: [
      { id: "chatgpt_search", monthlyVisitors: 450, acquisitionType: "organic_unknown" },
      { id: "perplexity_ai", monthlyVisitors: 300, acquisitionType: "organic_unknown" },
      { id: "google_organic", monthlyVisitors: 900, acquisitionType: "organic" },
    ],
    projectionInputs: { projectionMonths: DEFAULT_PROJECTION_MONTHS, monthlyVisitors: 1_650, revenuePerLead: 6_500, reinvestmentPercent: 6, reinvestmentCap: 5_000 },
    scenarioLift: { cvrMultiplier: 1.15, visitorMultiplier: 1.22, revenueMultiplier: 1.04 },
  },
  {
    id: "white-label-retainer-path",
    templateId: "agency-white-label",
    category: "agency",
    stages: [
      { id: "content", stageType: "blog-post", conversionRate: 44, dueInDays: 5 },
      { id: "audit", stageType: "calculator-page", conversionRate: 24, dueInDays: 8 },
      { id: "lead", stageType: "opt-in-page", conversionRate: 34, dueInDays: 10 },
      { id: "call", stageType: "discovery-call", conversionRate: 46, dueInDays: 15 },
      { id: "proposal", stageType: "proposal", conversionRate: 57, dueInDays: 21 },
      { id: "retainer", stageType: "retainer-close", conversionRate: 72, dueInDays: 28 },
    ],
    trafficChannels: [
      { id: "google_organic", monthlyVisitors: 850, acquisitionType: "organic" },
      { id: "linkedin", monthlyVisitors: 350, acquisitionType: "organic" },
      { id: "email_newsletter", monthlyVisitors: 250, acquisitionType: "organic" },
    ],
    projectionInputs: { projectionMonths: DEFAULT_PROJECTION_MONTHS, monthlyVisitors: 1_450, revenuePerLead: 4_000, reinvestmentPercent: 5, reinvestmentCap: 3_500 },
    scenarioLift: { cvrMultiplier: 1.13, visitorMultiplier: 1.12, revenueMultiplier: 1.05 },
  },
  {
    id: "compliance-subscription-path",
    templateId: "adult-compliance",
    category: "compliance",
    stages: [
      { id: "reach", stageType: "landing-page", conversionRate: 38, dueInDays: 6 },
      { id: "gate", stageType: "application-page", conversionRate: 62, dueInDays: 8 },
      { id: "content", stageType: "case-study", conversionRate: 31, dueInDays: 12 },
      { id: "lead", stageType: "opt-in-page", conversionRate: 22, dueInDays: 16 },
      { id: "checkout", stageType: "order-form", conversionRate: 18, dueInDays: 20 },
      { id: "save", stageType: "cancellation-save", conversionRate: 12, dueInDays: 28, requiredStage: false },
    ],
    trafficChannels: [
      { id: "google_organic", monthlyVisitors: 700, acquisitionType: "organic" },
      { id: "reddit_organic", monthlyVisitors: 350, acquisitionType: "organic" },
      { id: "display_ads", monthlyVisitors: 450, acquisitionType: "cpm", cpm: 6, ctr: 1.2 },
    ],
    projectionInputs: { projectionMonths: DEFAULT_PROJECTION_MONTHS, monthlyVisitors: 1_500, revenuePerLead: 850, reinvestmentPercent: 6, reinvestmentCap: 2_500 },
    scenarioLift: { cvrMultiplier: 1.08, visitorMultiplier: 1.14, revenueMultiplier: 1.06 },
  },
] as const;

export function getBlueprintPresetsForTemplate(templateId: FunnelTemplateId | null): readonly FunnelBlueprintPreset[] {
  if (!templateId) return FUNNEL_BLUEPRINT_PRESETS;
  return FUNNEL_BLUEPRINT_PRESETS.filter((preset) => preset.templateId === templateId);
}

export function getBlueprintPreset(id: string): FunnelBlueprintPreset | undefined {
  return FUNNEL_BLUEPRINT_PRESETS.find((preset) => preset.id === id);
}

export function buildBlueprintStages(
  preset: FunnelBlueprintPreset,
  labelForStageType: (stageType: string) => string,
): FunnelStageConfig[] {
  return preset.stages.map((stage, index) => ({
    id: `${preset.id}-${stage.id}`.slice(0, 40),
    name: labelForStageType(stage.stageType),
    conversionRate: stage.conversionRate,
    isEditable: true,
    stageType: stage.stageType,
    publishedStatus: "draft",
    requiredStage: stage.requiredStage ?? true,
    assetStatus: "draft",
    trackingStatus: "not_set",
    waitDays: stage.waitDays ?? 0,
    dueInDays: stage.dueInDays ?? 7 + index * 3,
    priority: index <= 2 ? "high" : "medium",
  }));
}

export function buildBlueprintChannels(
  preset: FunnelBlueprintPreset,
  labelForChannel: (channelId: string) => string,
): TrafficChannel[] {
  return preset.trafficChannels.map((channel) => ({
    ...channel,
    id: channel.id,
    label: labelForChannel(channel.id),
    trackingSetup: "utm_only",
    device: "all",
    rampType: "flat",
    confidence: "medium",
  }));
}

export function buildBlueprintScenarios(
  preset: FunnelBlueprintPreset,
  baseScenarios: ScenarioSnapshot[],
  scenarioName: string,
  scenarioNotes: string,
  changedAssumptions?: string[],
): ScenarioSnapshot[] {
  const customScenario: ScenarioSnapshot = {
    id: `${preset.id}-optimization`.slice(0, 60),
    name: scenarioName.slice(0, 80),
    kind: "custom",
    cvrMultiplier: preset.scenarioLift.cvrMultiplier,
    visitorMultiplier: preset.scenarioLift.visitorMultiplier,
    revenueMultiplier: preset.scenarioLift.revenueMultiplier,
    changedAssumptions: changedAssumptions ?? [
      `CVR x${preset.scenarioLift.cvrMultiplier.toFixed(2)}`,
      `Traffic x${preset.scenarioLift.visitorMultiplier.toFixed(2)}`,
      `Revenue yield x${preset.scenarioLift.revenueMultiplier.toFixed(2)}`,
    ],
    notes: scenarioNotes.slice(0, 280),
  };

  return [...baseScenarios, customScenario].slice(0, 8);
}
